import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { augmontOrderService } from '../services/augmontOrderService';
import { getCurrentUser } from '../utils/auth';

interface OrderStatus {
  order_id: string;
  order_status: string;
  payment_url?: string;
  order_details: {
    invoice_number: string;
    transaction_id: string;
    amount: number;
    quantity: number;
    rate: number;
  };
}

// Transaction interface removed because it's unused

const OrderStatusPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);
  // Removed unused 'transaction' state to satisfy linter
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const currentUser = getCurrentUser();

  const fetchOrderStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await augmontOrderService.getOrderStatus(orderId!);
      
      if (response.success && response.data) {
        setOrderStatus(response.data);
      } else {
        setError(response.error || 'Failed to fetch order status');
      }
    } catch (err) {
      setError('Failed to fetch order status');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) {
      fetchOrderStatus();
    }
  }, [orderId, fetchOrderStatus]);

  const refreshStatus = async () => {
    setRefreshing(true);
    await fetchOrderStatus();
    setRefreshing(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return '✅';
      case 'pending':
        return '⏳';
      case 'failed':
      case 'cancelled':
        return '❌';
      default:
        return '📋';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePayment = () => {
    if (orderStatus?.payment_url) {
      window.open(orderStatus.payment_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/90 backdrop-blur-sm flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-800 mx-auto mb-4"></div>
          <p className="text-gray-800 text-lg">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white/90 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/users/donation')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
              >
                Back to Donation
              </button>
              <button
                onClick={() => navigate('/users/dashboard')}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white/90 backdrop-blur-sm py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Order Status</h1>
          <p className="text-gray-600">Track your gold donation order</p>
        </div>

        {/* Order Status Card */}
        <div className="bg-white rounded-lg shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Order #{orderId}</h2>
              <p className="text-gray-600">Created by {currentUser?.user_name}</p>
            </div>
            <button
              onClick={refreshStatus}
              disabled={refreshing}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
            >
              {refreshing ? '🔄' : '🔄'} Refresh
            </button>
          </div>

          {/* Status Display */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{getStatusIcon(orderStatus?.order_status || 'pending')}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Order Status</h3>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(orderStatus?.order_status || 'pending')}`}>
                    {orderStatus?.order_status || 'Pending'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Last Updated</p>
                <p className="text-sm font-medium text-gray-800">
                  {orderStatus ? formatDate(new Date().toISOString()) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Order Details */}
          {orderStatus && (
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Order Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invoice Number:</span>
                    <span className="font-medium">{orderStatus.order_details.invoice_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium">{orderStatus.order_details.transaction_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">₹{orderStatus.order_details.amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Quantity:</span>
                    <span className="font-medium">{orderStatus.order_details.quantity} grams</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rate per gram:</span>
                    <span className="font-medium">₹{orderStatus.order_details.rate.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-3">Payment Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-medium">Augmont</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Currency:</span>
                    <span className="font-medium">INR</span>
                  </div>
                  {orderStatus.payment_url && (
                    <div className="pt-2">
                      <button
                        onClick={handlePayment}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                      >
                        💳 Proceed to Payment
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/users/donation')}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              💰 Make Another Donation
            </button>
            <button
              onClick={() => navigate('/users/orders')}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              📋 View All Orders
            </button>
            <button
              onClick={() => navigate('/users/dashboard')}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              🏠 Back to Dashboard
            </button>
          </div>
        </div>

        {/* Status Timeline */}
        <div className="bg-white rounded-lg shadow-xl p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Order Timeline</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                ✓
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">Order Created</p>
                <p className="text-sm text-gray-600">Order has been successfully created</p>
              </div>
              <span className="text-sm text-gray-500">Now</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                orderStatus?.order_status === 'pending' ? 'bg-yellow-500' : 'bg-gray-300'
              }`}>
                {orderStatus?.order_status === 'pending' ? '⏳' : '○'}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">Payment Processing</p>
                <p className="text-sm text-gray-600">Payment is being processed</p>
              </div>
              <span className="text-sm text-gray-500">Next</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                orderStatus?.order_status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                {orderStatus?.order_status === 'completed' ? '✓' : '○'}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">Order Completed</p>
                <p className="text-sm text-gray-600">Gold has been purchased successfully</p>
              </div>
              <span className="text-sm text-gray-500">Final</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusPage; 